import React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { brandLogo, brandName, bundledLogo, logoErrorFallback } from '../../_metronic/layout/brandLogo'
import { privacyContentFor, PRIVACY_UPDATED_ISO } from './privacy/privacyPolicyContent'

const GOOGLE_USER_DATA_POLICY = 'https://developers.google.com/terms/api-services-user-data-policy'
const GOOGLE_PERMISSIONS = 'https://myaccount.google.com/permissions'

/**
 * The app's privacy policy, public (signed in or not) at /privacy-policy, in
 * the UI language. It stands outside the app shell, so it carries its own
 * logo and a way back.
 */
const PrivacyPolicy: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const content = privacyContentFor(intl.locale)
  const companyName = brandName(process.env.REACT_APP_COMPANY_NAME)
  const email = process.env.REACT_APP_COMPANY_EMAIL
  const website = process.env.REACT_APP_COMPANY_WEBSITE

  const goBack = () => {
    // Opened in its own tab from the footer there is nothing to go back to.
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }

  return (
    <div className='d-flex flex-column flex-root bg-body min-vh-100' data-testid='privacy-policy'>
      <div className='container py-10' style={{ maxWidth: 820 }}>
        <div className='d-flex align-items-center justify-content-between mb-10'>
          <img
            alt={companyName}
            src={brandLogo(process.env.REACT_APP_CDN_DOMAIN, 'wide', 'light')}
            onError={logoErrorFallback(bundledLogo('wide', 'light'))}
            className='h-35px'
          />
          <button type='button' className='btn btn-sm btn-light' onClick={goBack}>
            <i className='ki-duotone ki-arrow-left fs-3'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            {content.back}
          </button>
        </div>

        <article className='card'>
          <div className='card-body p-8 p-lg-12 fs-6 text-gray-700'>
            <h1 className='fs-2hx fw-bold text-gray-900 mb-2'>{content.title}</h1>
            <time className='d-block text-muted fs-7 mb-8' dateTime={PRIVACY_UPDATED_ISO}>
              {content.updated}
            </time>
            <p className='mb-8'>{content.intro}</p>

            {content.sections.map((section) => (
              <section key={section.title} className='mb-8'>
                <h2 className='fs-3 fw-bold text-gray-900 mb-4'>{section.title}</h2>
                {section.items && (
                  <ul className='ps-5 mb-4'>
                    {section.items.map((item) => (
                      <li key={item} className='mb-2'>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className='mb-3'>
                    {paragraph}
                  </p>
                ))}
                {section.googleLinks && (
                  <p className='d-flex flex-wrap gap-5 mb-0'>
                    <a href={GOOGLE_USER_DATA_POLICY} target='_blank' rel='noopener noreferrer'>
                      {content.googlePolicyLink}
                    </a>
                    <a href={GOOGLE_PERMISSIONS} target='_blank' rel='noopener noreferrer'>
                      {content.googlePermissionsLink}
                    </a>
                  </p>
                )}
              </section>
            ))}

            <section>
              <h2 className='fs-3 fw-bold text-gray-900 mb-4'>{content.contactTitle}</h2>
              <ul className='list-unstyled mb-0'>
                <li className='mb-2'>
                  {content.contactCompany}: <strong>{companyName}</strong>
                </li>
                {email && (
                  <li className='mb-2'>
                    {content.contactEmail}: <a href={`mailto:${email}`}>{email}</a>
                  </li>
                )}
                {website && (
                  <li>
                    {content.contactWebsite}:{' '}
                    <a href={website} target='_blank' rel='noopener noreferrer'>
                      {website.replace(/^https?:\/\//, '')}
                    </a>
                  </li>
                )}
              </ul>
            </section>
          </div>
        </article>
      </div>
    </div>
  )
}

export default PrivacyPolicy
